"use client"

import * as React from "react"
import { Calendar as CalendarIcon, X } from "lucide-react"
import { type DateRange } from "react-day-picker"
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
  format,
  isSameDay,
} from "date-fns"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { calendarLocaleFor } from "@/components/ui/calendar-locale"
import { Separator } from "@/components/ui/separator"
import { useShellTranslation, type ShellTranslate } from "@/lib/shell/use-shell-translation"
import { dayToCell } from "@/lib/time/calendar-cell"
import { todayInZone } from "@/lib/time/zoned-day"

// ── Preset types ──

export interface DateRangePreset {
  /** English label; shown as is when there is no `labelKey`. */
  label: string
  /** Catalogue key of `label`: the built-in presets speak the interface language. */
  labelKey?: string
  /**
   * The range, counted from `now` (default: the viewer's clock). A picker
   * given a `timeZone` passes that zone's today as a calendar cell.
   */
  getValue: (now?: Date) => DateRange
}

// ── Default presets ──

export const DEFAULT_PRESETS: DateRangePreset[] = [
  {
    label: "Today",
    labelKey: "shell.dateRange.presets.today",
    getValue: (now = new Date()) => ({
      from: startOfDay(now),
      to: endOfDay(now),
    }),
  },
  {
    label: "Yesterday",
    labelKey: "shell.dateRange.presets.yesterday",
    getValue: (now = new Date()) => {
      const yesterday = subDays(now, 1)
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) }
    },
  },
  {
    label: "This Week",
    labelKey: "shell.dateRange.presets.thisWeek",
    getValue: (now = new Date()) => ({
      from: startOfWeek(now, { weekStartsOn: 1 }),
      to: endOfDay(now),
    }),
  },
  {
    label: "Last Week",
    labelKey: "shell.dateRange.presets.lastWeek",
    getValue: (now = new Date()) => {
      const lastWeek = subWeeks(now, 1)
      return {
        from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      }
    },
  },
  {
    label: "This Month",
    labelKey: "shell.dateRange.presets.thisMonth",
    getValue: (now = new Date()) => ({
      from: startOfMonth(now),
      to: endOfDay(now),
    }),
  },
  {
    label: "Last Month",
    labelKey: "shell.dateRange.presets.lastMonth",
    getValue: (now = new Date()) => {
      const lastMonth = subMonths(now, 1)
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) }
    },
  },
  {
    label: "This Quarter",
    labelKey: "shell.dateRange.presets.thisQuarter",
    getValue: (now = new Date()) => ({
      from: startOfQuarter(now),
      to: endOfDay(now),
    }),
  },
  {
    label: "Last Quarter",
    labelKey: "shell.dateRange.presets.lastQuarter",
    getValue: (now = new Date()) => {
      const lastQ = subQuarters(now, 1)
      return { from: startOfQuarter(lastQ), to: endOfQuarter(lastQ) }
    },
  },
]

/**
 * "Now" for the presets. With no `timeZone`, the viewer's clock, as it always
 * was. With one, that zone's today as a calendar cell (local midnight of the
 * zone's date): a shared dashboard counts the WORKSPACE's days (owner decision
 * 2026-09-26), so "Today" is the workspace's today. On the viewer's clock an
 * admin in India between 00:00 and 01:30 IST on a Dubai workspace picked Today
 * and got Dubai's tomorrow.
 */
export function presetAnchor(timeZone?: string, now: Date = new Date()): Date {
  if (!timeZone) return now
  return dayToCell(todayInZone(timeZone, now)) ?? now
}

/**
 * A preset's range. On a zone, it is counted from that zone's today and
 * committed as whole local days, the shape a calendar pick has, so reading it
 * back with `cellToDay` gives the zone's dates. With none, exactly as before.
 */
export function presetRange(
  preset: Pick<DateRangePreset, "getValue">,
  timeZone?: string,
): DateRange {
  return timeZone
    ? toWholeLocalDays(preset.getValue(presetAnchor(timeZone)))
    : preset.getValue()
}

// ── Date field option ──

export interface DateFieldOption {
  key: string
  label: string
}

// ── Props ──

interface DateRangePickerProps {
  /** Current date range */
  value: DateRange | undefined
  /** Called when range changes */
  onChange: (range: DateRange | undefined) => void
  /** Placeholder text. Default: "Pick a date range", in the interface language */
  placeholder?: string
  /** Popover alignment. Default: "start" */
  align?: "start" | "center" | "end"
  /** Extra className for trigger button */
  className?: string
  /** Show preset sidebar. Default: true */
  showPresets?: boolean
  /** Custom presets (replaces defaults) */
  presets?: DateRangePreset[]
  /** Disable dates after today. Default: false */
  disableFutureDates?: boolean
  /** Number of calendar months. Default: 2 */
  numberOfMonths?: 1 | 2
  /** Optional date field tabs (e.g. Date Added, Hire Date, Modified Date) */
  dateFieldOptions?: DateFieldOption[]
  /** Currently selected date field key */
  activeDateField?: string
  /** Called when date field tab changes */
  onDateFieldChange?: (key: string) => void
  /**
   * The zone whose days the page counts (a shared dashboard passes the
   * workspace's, from `useWorkspaceTimeZone`). "Today" and every other preset
   * then count from that zone's today, and so does `disableFutureDates`.
   * Unset: the viewer's clock.
   */
  timeZone?: string
}

// ── Helpers ──

/**
 * Compact label for the filter-chip trigger button.
 *
 * Goal: keep the trigger narrow (≤ ~22 chars) so it reads as a standard
 * filter chip, not a data cell. The year is dropped whenever it matches
 * the current year; same-month ranges collapse to `Apr 1–15`.
 *
 * The POPOVER still shows the full verbose range inside its body.
 *
 *   From  Apr 1              (same day, same year)
 *   Apr 3 – 15               (same month + year)
 *   Apr 28 – May 3           (same year, different months)
 *   Dec 28 2025 – Jan 4 2026 (cross-year — years shown)
 *
 * In any interface language but English the dates are written by Intl in that
 * language, by the same rules (formatLocalizedDayRange); English keeps these
 * date-fns shapes exactly.
 */
function formatDateRange(
  range: DateRange | undefined,
  t: ShellTranslate,
  locale: string = "en",
): string | null {
  if (!range?.from) return null
  if (!isEnglish(locale)) {
    const dates = formatLocalizedDayRange(range.from, range.to, locale)
    // An open-ended range is "From {date}" in the language's own words ("من 20 سبتمبر").
    if (dates !== null) return range.to ? dates : t("shell.dateRange.from", { date: dates })
  }
  const currentYear = new Date().getFullYear()
  if (!range.to) {
    const y = range.from.getFullYear() !== currentYear ? ", yyyy" : ""
    return t("shell.dateRange.from", { date: format(range.from, `MMM d${y}`) })
  }
  if (isSameDay(range.from, range.to)) {
    const y = range.from.getFullYear() !== currentYear ? ", yyyy" : ""
    return format(range.from, `MMM d${y}`)
  }
  const fromYear = range.from.getFullYear()
  const toYear = range.to.getFullYear()
  if (fromYear !== toYear) {
    return `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`
  }
  const ySuffix = fromYear !== currentYear ? `, ${fromYear}` : ""
  if (range.from.getMonth() === range.to.getMonth()) {
    return `${format(range.from, "MMM d")}–${format(range.to, "d")}${ySuffix}`
  }
  return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d")}${ySuffix}`
}

function isEnglish(locale: string): boolean {
  return locale === "en" || locale.startsWith("en-")
}

/**
 * A day range for a date chip in the interface language: "20–26 سبتمبر",
 * "20 – 26 सित॰", "20–26 sept.". The rule of every date chip that speaks the
 * interface language (this picker's, the shared DateRangeFilter's, the phishing
 * report controls'): Intl writes each date the language's way, in Western digits
 * as the catalogues do (lib/i18n/format-message.ts), and `formatRange` joins a
 * range the way the language joins one; the English month abbreviations of
 * date-fns sat inside translated words ("من Sep 20").
 *
 * `from` and `to` are calendar cells (any time on the day they name, read in the
 * viewer's zone); no `to` (or the same day) is one date. `year`: `'auto'` names it
 * only when it is not the current year or the range crosses one; `'always'` names
 * it every time (the report chips, where a one-year window must never read
 * "Jul 30 – Jul 30", feedback #81).
 *
 * Null for English, where each chip keeps its own English shape, and when Intl
 * cannot format (the caller then writes English). Re-exported by
 * components/shared/date-range-filter.tsx, where the report controls import it.
 */
export function formatLocalizedDayRange(
  from: Date,
  to: Date | undefined,
  locale: string,
  year: "auto" | "always" = "auto",
): string | null {
  if (isEnglish(locale)) return null
  try {
    const currentYear = new Date().getFullYear()
    const withYear =
      year === "always" ||
      from.getFullYear() !== currentYear ||
      (to !== undefined && to.getFullYear() !== from.getFullYear())
    const formatter = new Intl.DateTimeFormat(`${locale}-u-nu-latn`, {
      ...(withYear ? { year: "numeric" as const } : {}),
      month: "short",
      day: "numeric",
    })
    if (!to || isSameDay(from, to)) return formatter.format(from)
    return typeof formatter.formatRange === "function"
      ? formatter.formatRange(from, to)
      : `${formatter.format(from)} – ${formatter.format(to)}`
  } catch {
    return null
  }
}

/**
 * A picked range as whole local days: `from` is the first instant of its first
 * day and `to` the last millisecond of its last day. A missing bound stays
 * missing (an open-ended range).
 *
 * react-day-picker hands back each clicked day at LOCAL MIDNIGHT. Committed as
 * is, every inclusive `lte(col, to)` behind a table filter dropped the last
 * picked day and a one-day pick (from === to) matched nothing: 15 screens, in
 * every zone (locale/time-zone audit, 2026-09-26). `endOfDay` is calendar math,
 * so a 25-hour DST day is still one day. Idempotent for a committed range.
 */
export function toWholeLocalDays<R extends { from?: Date; to?: Date }>(range: R): R {
  return {
    ...range,
    from: range.from ? startOfDay(range.from) : range.from,
    to: range.to ? endOfDay(range.to) : range.to,
  }
}

function isPresetActive(
  preset: DateRangePreset,
  value: DateRange | undefined,
  timeZone?: string,
): boolean {
  if (!value?.from || !value?.to) return false
  const presetValue = presetRange(preset, timeZone)
  if (!presetValue.from || !presetValue.to) return false
  return isSameDay(value.from, presetValue.from) && isSameDay(value.to, presetValue.to)
}

// ── Component ──

export function DateRangePicker({
  value,
  onChange,
  placeholder: placeholderProp,
  align = "start",
  className,
  showPresets = true,
  presets = DEFAULT_PRESETS,
  disableFutureDates = false,
  numberOfMonths = 1,
  dateFieldOptions,
  activeDateField,
  onDateFieldChange,
  timeZone,
}: DateRangePickerProps) {
  // The chip's own words speak the interface language (English outside the
  // provider); a page's placeholder and its own presets keep their wording.
  const { t, locale } = useShellTranslation()
  const placeholder = placeholderProp ?? t("shell.dateRange.placeholder")
  const presetLabel = (preset: DateRangePreset) => (preset.labelKey ? t(preset.labelKey) : preset.label)
  const [open, setOpen] = React.useState(false)
  // Draft mirrors `value` while the popover is open so Cancel can revert.
  // Presets bypass the draft and commit immediately (that's the point of a
  // preset). Reset clears + commits + closes.
  // #414 — dropdown bounds. Wide by default so a distant created/modified date
  // is reachable; `disableFutureDates` callers cap at today, matching the
  // `disabled` rule above so the dropdown never offers a month the grid refuses.
  // "Today" is the `timeZone`'s when the page names one.
  const startMonth = React.useMemo(
    () => new Date(new Date().getFullYear() - 100, 0, 1),
    [],
  )
  const endMonth = React.useMemo(() => {
    const today = presetAnchor(timeZone)
    return disableFutureDates
      ? new Date(today.getFullYear(), today.getMonth(), 1)
      : new Date(today.getFullYear() + 20, 11, 1)
  }, [disableFutureDates, timeZone])

  const [draft, setDraft] = React.useState<DateRange | undefined>(value)

  // Sync draft whenever the popover opens OR the external value changes.
  React.useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const label = formatDateRange(value, t, locale)
  const isActive = !!value?.from
  const activeFieldLabel = dateFieldOptions?.find(o => o.key === activeDateField)?.label

  const handlePresetClick = (preset: DateRangePreset) => {
    onChange(presetRange(preset, timeZone))
    setOpen(false)
  }

  const handleRangeSelect = (range: DateRange | undefined) => {
    // Draft-only update — user clicks Apply to commit.
    setDraft(range)
  }

  const handleApply = () => {
    // Presets already commit whole days; a calendar pick is local midnights.
    onChange(draft ? toWholeLocalDays(draft) : undefined)
    setOpen(false)
  }

  const handleReset = () => {
    setDraft(undefined)
    onChange(undefined)
    setOpen(false)
  }

  const handleCancel = () => {
    // Revert any uncommitted draft edits, close without saving.
    setDraft(value)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            // Compact filter-chip shape. Hard max-width so cross-year ranges
            // (e.g. "Dec 28, 2025 – Jan 4, 2026") truncate with ellipsis instead
            // of stretching the toolbar. The full range stays readable in the
            // popover body + native title-tooltip.
            "h-9 max-w-60 justify-start gap-1.5 text-start font-normal border-dashed",
            isActive && "bg-secondary border-solid",
            className,
          )}
          title={
            label
              ? activeFieldLabel
                ? `${activeFieldLabel}: ${label}`
                : label
              : placeholder
          }
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
          {/* Filter title — always shown so the chip reads as a filter, not a
              data value. Matches the multi-select filter chip shape. */}
          <span className="shrink-0 font-medium">
            {activeFieldLabel ?? placeholder}
          </span>
          {label && (
            <>
              <span className="text-muted-foreground/50" aria-hidden>·</span>
              <span className="truncate text-muted-foreground">{label}</span>
            </>
          )}
          {isActive && (
            <span
              role="button"
              tabIndex={-1}
              title={t("shell.dateRange.clear")}
              className="ms-0.5 inline-flex shrink-0 items-center rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onPointerDown={(e) => {
                // Prevent Radix Popover trigger from receiving the event
                e.stopPropagation()
                e.preventDefault()
              }}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onChange(undefined)
                setOpen(false)
              }}
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align={align}
        // Wire the popover to the window via Radix Portal (default) and cap
        // its height so overflow scrolls inside the popover instead of being
        // clipped by ancestor containers. 520px fits two months + footer on
        // most viewports; 85vh is the hard ceiling for small screens.
        collisionPadding={12}
      >
        <div className="flex flex-col max-h-[min(85vh,540px)]">
          {/* Deprecated field-tabs header — only renders for legacy callers that
              still pass dateFieldOptions. New consumers should render one
              DateFieldFilter chip per field instead. See
              project_date_filter_chip_spec.md. */}
          {dateFieldOptions && dateFieldOptions.length > 1 && (
            <div className="flex items-center gap-1 border-b px-3 py-2 shrink-0">
              {dateFieldOptions.map((opt) => (
                <Button
                  key={opt.key}
                  variant={activeDateField === opt.key ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs px-3 rounded-full"
                  onClick={() => onDateFieldChange?.(opt.key)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          )}

          {/* Scrollable body — presets sidebar + calendar. Footer stays pinned
              so Apply/Cancel/Reset are always reachable. */}
          <div className="flex overflow-y-auto">
            {showPresets && (
              <>
                <div className="flex flex-col gap-0.5 p-2 w-36 shrink-0">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground px-2 py-1">
                    {t("shell.dateRange.quickSelect")}
                  </p>
                  {presets.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "justify-start h-7 px-2 text-xs font-normal",
                        isPresetActive(preset, draft, timeZone) && "bg-accent font-medium",
                      )}
                      onClick={() => handlePresetClick(preset)}
                    >
                      {presetLabel(preset)}
                    </Button>
                  ))}
                </div>
                <Separator orientation="vertical" />
              </>
            )}
            <div className="p-2">
              <Calendar
                mode="range"
                // Month and weekday names in the interface language (browser QA 2026-10-05).
                locale={calendarLocaleFor(locale)}
                selected={draft}
                onSelect={handleRangeSelect}
                numberOfMonths={numberOfMonths}
                disabled={disableFutureDates ? { after: presetAnchor(timeZone) } : undefined}
                // #414 — without captionLayout + explicit month bounds,
                // react-day-picker v9 falls back to "label" and the only way to
                // move is ±1 month via the chevrons. CustomDatePicker already
                // carries this and records why: "production DOB bug 2026-05-28:
                // birth year from 1980s needed 500+ clicks". Same documented
                // fix, applied to the shared primitive every table filter uses.
                captionLayout="dropdown"
                startMonth={startMonth}
                endMonth={endMonth}
                initialFocus
              />
            </div>
          </div>

          {/* Footer — three buttons: Reset | Cancel | Apply. Reset + Cancel on
              the left as ghost/outline, Apply on the right as primary. Reset
              is disabled when nothing is committed AND the draft is empty. */}
          <div className="flex items-center justify-between gap-2 border-t px-3 py-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-muted-foreground"
              onClick={handleReset}
              disabled={!isActive && !draft?.from}
            >
              <X className="h-3.5 w-3.5 me-1" />
              {t("shell.table.reset")}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleCancel}
              >
                {t("common.cancel")}
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={handleApply}
              >
                {t("shell.dateRange.apply")}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
